import {
  getDocumentStatusChangeEligibility,
  getDocumentStatusSkipMessage,
  GQL,
} from '@aline/common';
import type { DocumentStatusChangeEligibility } from '@aline/common';
import { Apollo } from 'src/gql/generated';

import type { DocumentStatusJobPhase } from './document-status-jobs';

const STATUS_OPTIONS = [
  Apollo.DocumentStatus.Draft,
  Apollo.DocumentStatus.Signed,
  Apollo.DocumentStatus.Expired,
  Apollo.DocumentStatus.Template,
] as const;

const GQL_ENVELOPE_STATUS = {
  [Apollo.EnvelopeStatus.Completed]: GQL.EnvelopeStatus.Completed,
  [Apollo.EnvelopeStatus.Created]: GQL.EnvelopeStatus.Created,
  [Apollo.EnvelopeStatus.Declined]: GQL.EnvelopeStatus.Declined,
  [Apollo.EnvelopeStatus.Delivered]: GQL.EnvelopeStatus.Delivered,
  [Apollo.EnvelopeStatus.Drafted]: GQL.EnvelopeStatus.Drafted,
  [Apollo.EnvelopeStatus.Sent]: GQL.EnvelopeStatus.Sent,
  [Apollo.EnvelopeStatus.Signed]: GQL.EnvelopeStatus.Signed,
  [Apollo.EnvelopeStatus.Voided]: GQL.EnvelopeStatus.Voided,
} satisfies Record<Apollo.EnvelopeStatus, GQL.EnvelopeStatus>;

// Rounds down, so a job that is nearly done never reads as finished.
const COMPACT_COUNT_FORMAT = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  notation: 'compact',
  roundingMode: 'floor',
});

export interface GridDocumentStatusInput {
  isCertificate: boolean;
  isSigned: boolean;
  latestVersion: DocumentStatusVersion;
  requesterRole: Apollo.PermissionRole | null;
  status: Apollo.DocumentStatus | null;
  variant: Apollo.DocumentVariant;
  workflow: Pick<
    Apollo.Workflow,
    'envelopeStatus' | 'isSignedOutsideAline' | 'wasSentForSig'
  > | null;
  workflowFileRole: Apollo.WorkflowFileDocumentRole | null;
}

interface DocumentStatusVersion {
  __typename: string;
  fileLocation?: { mimetype: string | null } | null;
}

export interface GridDocumentStatusMenuItem {
  eligibility: DocumentStatusChangeEligibility;
  status: Apollo.DocumentStatus;
  tooltip: string;
}

export function getGridDocumentStatusMenuItems(
  row: GridDocumentStatusInput
): GridDocumentStatusMenuItem[] {
  const document = {
    isActive: row.status !== Apollo.DocumentStatus.Expired,
    isCertificate: row.isCertificate,
    isSigned: row.isSigned,
    variant: toGqlVariant(row.variant),
  };
  const workflow = row.workflow
    ? {
        envelopeStatus: GQL_ENVELOPE_STATUS[row.workflow.envelopeStatus],
        isSignedOutsideAline: row.workflow.isSignedOutsideAline,
        isSigningFile: row.workflowFileRole === Apollo.WorkflowFileDocumentRole.Signing,
        wasSentForSig: row.workflow.wasSentForSig,
      }
    : null;
  const mimetype = getRowMimetype(row.latestVersion);
  const isEditor = row.requesterRole === Apollo.PermissionRole.Editor;

  return STATUS_OPTIONS.map(status => {
    const eligibility = getDocumentStatusChangeEligibility({
      document,
      isEditor,
      mimetype,
      targetStatus: toGqlStatus(status),
      workflow,
    });
    const tooltip =
      eligibility.outcome === 'skip' ? getDocumentStatusSkipMessage(eligibility.reason) : '';

    return { eligibility, status, tooltip };
  });
}

export function getGridRowCanEditStatus(row: GridDocumentStatusInput): boolean {
  return row.requesterRole === Apollo.PermissionRole.Editor;
}

export function getStatusChangeNeedsConfirm(
  current: Apollo.DocumentStatus,
  target: Apollo.DocumentStatus
): boolean {
  if (target === Apollo.DocumentStatus.Template) return true;
  return target === Apollo.DocumentStatus.Draft && current === Apollo.DocumentStatus.Template;
}

export function formatSetDocumentsStatusMessage(result: {
  numSuccess: number;
  skipCounts: readonly { count: number; reason: Apollo.DocumentStatusSkipReason }[];
  status: Apollo.DocumentStatus;
}): string {
  const label = statusLabel(result.status);
  const updated =
    result.numSuccess > 0 ? `Updated ${documentCount(result.numSuccess)} to ${label}.` : null;
  const skips = result.skipCounts.filter(skip => skip.count > 0);
  if (!updated && skips.length === 0)
    return `No documents were updated. They are already set to ${label}.`;

  const skipped = formatSkippedDocuments(skips);
  if (!updated) return skipped;
  if (!skipped) return updated;

  return `${updated} ${skipped}`;
}

export function formatDocumentStatusJobTitle(job: {
  numQueued: number;
  phase: DocumentStatusJobPhase;
  status: Apollo.DocumentStatus;
}): string {
  const label = statusLabel(job.status);
  switch (job.phase) {
    case 'running':
      return `Updating ${compactDocumentCount(job.numQueued)} to ${label}`;
    case 'cancelling':
      return `Stopping the update to ${label}`;
    case 'done':
      return `Finished updating to ${label}`;
    case 'cancelled':
      return `Stopped updating to ${label}`;
  }
}

export function formatDocumentStatusJobProgress(job: {
  numFinished: number;
  numQueued: number;
}): string {
  const numFinished = COMPACT_COUNT_FORMAT.format(Math.min(job.numFinished, job.numQueued));
  return `${numFinished} of ${compactDocumentCount(job.numQueued)} updated`;
}

export function formatDocumentStatusJobDoneMessage(summary: {
  numFailed: number;
  numNotRun: number;
  numSuccess: number;
  skipCounts: readonly { count: number; reason: Apollo.DocumentStatusSkipReason }[];
  status: Apollo.DocumentStatus;
}): string {
  const outcomes = [formatDocumentStatusJobOutcome(summary)];
  if (summary.numFailed > 0)
    outcomes.push(
      `${documentCount(summary.numFailed)} couldn't be updated because of an error. Try again.`
    );
  if (summary.numNotRun > 0)
    outcomes.push(`${documentCount(summary.numNotRun)} were left as they were.`);

  return outcomes.filter(Boolean).join(' ');
}

export function patchSetDocumentsStatusCache(
  cache: DocumentStatusCache,
  result: {
    documentIds: readonly string[];
    isActive: boolean;
    isSigned: boolean | null;
    status: Apollo.DocumentStatus;
    variant: Apollo.DocumentVariant | null;
  }
): void {
  cache.batch({
    update(batchCache) {
      for (const documentId of result.documentIds) {
        const id = batchCache.identify({ __typename: 'Document', id: documentId });
        if (!id) continue;

        batchCache.modify({
          fields: {
            isActive: () => result.isActive,
            isSigned: existing => result.isSigned ?? existing,
            status: () => result.status,
            variant: existing => result.variant ?? existing,
          },
          id,
        });
      }
    },
  });
}

interface DocumentStatusCache {
  batch(options: { update(cache: DocumentStatusCache): void }): void;
  identify(object: { __typename: 'Document'; id: string }): string | undefined;
  modify(options: {
    fields: {
      isActive(): boolean;
      isSigned(existing: unknown): unknown;
      status(): Apollo.DocumentStatus;
      variant(existing: unknown): unknown;
    };
    id: string;
  }): void;
}

export function getDisplayedDocumentStatus(
  status: Apollo.DocumentStatus | null
): Apollo.DocumentStatus {
  return status ?? Apollo.DocumentStatus.Draft;
}

function getRowMimetype(latestVersion: DocumentStatusVersion): string | null {
  if (latestVersion.__typename !== 'StaticFileVersion') return null;
  return latestVersion.fileLocation?.mimetype ?? null;
}

function toGqlStatus(status: Apollo.DocumentStatus): GQL.DocumentStatus {
  switch (status) {
    case Apollo.DocumentStatus.Draft:
      return GQL.DocumentStatus.Draft;
    case Apollo.DocumentStatus.Signed:
      return GQL.DocumentStatus.Signed;
    case Apollo.DocumentStatus.Expired:
      return GQL.DocumentStatus.Expired;
    case Apollo.DocumentStatus.Template:
      return GQL.DocumentStatus.Template;
  }
}

function toGqlVariant(variant: Apollo.DocumentVariant): GQL.DocumentVariant {
  switch (variant) {
    case Apollo.DocumentVariant.Document:
      return GQL.DocumentVariant.Document;
    case Apollo.DocumentVariant.Template:
      return GQL.DocumentVariant.Template;
  }
}

function statusLabel(status: Apollo.DocumentStatus): string {
  switch (status) {
    case Apollo.DocumentStatus.Draft:
      return 'Draft';
    case Apollo.DocumentStatus.Signed:
      return 'Signed';
    case Apollo.DocumentStatus.Expired:
      return 'Expired';
    case Apollo.DocumentStatus.Template:
      return 'Template';
  }
}

function documentCount(count: number): string {
  return count === 1 ? '1 document' : `${formatCount(count)} documents`;
}

// For counts that describe work in progress; a finished job's summary stays exact.
function compactDocumentCount(count: number): string {
  return `${COMPACT_COUNT_FORMAT.format(count)} ${count === 1 ? 'document' : 'documents'}`;
}

function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

/**
 * The finished-job summary leads with what changed. When a failure or a cancel
 * explains why nothing did, the "already set" line would mislead, so it is dropped.
 */
function formatDocumentStatusJobOutcome(summary: {
  numFailed: number;
  numNotRun: number;
  numSuccess: number;
  skipCounts: readonly { count: number; reason: Apollo.DocumentStatusSkipReason }[];
  status: Apollo.DocumentStatus;
}): string {
  const hasChanges = summary.numSuccess > 0 || summary.skipCounts.some(skip => skip.count > 0);
  if (!hasChanges && summary.numFailed + summary.numNotRun > 0) return '';

  return formatSetDocumentsStatusMessage(summary);
}

function formatSkippedDocuments(
  skips: readonly { count: number; reason: Apollo.DocumentStatusSkipReason }[]
): string {
  const total = skips.reduce((sum, skip) => sum + skip.count, 0);
  if (total === 0) return '';

  const lead = `${documentCount(total)} couldn't be changed`;
  if (skips.length === 1) return `${lead} because ${skipBecause(skips[0].count, skips[0].reason)}.`;

  const details = skips.map(skip => `${skip.count} ${skipDetail(skip.count, skip.reason)}`);
  return `${lead}: ${joinList(details)}.`;
}

function skipBecause(count: number, reason: Apollo.DocumentStatusSkipReason): string {
  const one = count === 1;
  switch (reason) {
    case Apollo.DocumentStatusSkipReason.AttachedToWorkflow:
      return one ? 'it is a signing file in a workflow' : 'they are signing files in a workflow';
    case Apollo.DocumentStatusSkipReason.CannotSignTemplate:
      return one
        ? "it is a template and can't be marked signed"
        : "they are templates and can't be marked signed";
    case Apollo.DocumentStatusSkipReason.NotAuthorized:
      return one ? "you can't edit it" : "you can't edit them";
    case Apollo.DocumentStatusSkipReason.NotPdf:
      return one ? 'it is not a PDF' : 'they are not PDFs';
    case Apollo.DocumentStatusSkipReason.SignedOrCertificate:
      return one ? 'it is signed or a certificate' : 'they are signed or certificates';
    case Apollo.DocumentStatusSkipReason.SigningWithinAline:
      return one ? 'an Aline workflow sets its status' : 'an Aline workflow sets their status';
    case Apollo.DocumentStatusSkipReason.WorkflowSentForSignature:
      return one ? 'it was already sent for signature' : 'they were already sent for signature';
  }
}

function skipDetail(count: number, reason: Apollo.DocumentStatusSkipReason): string {
  const one = count === 1;
  switch (reason) {
    case Apollo.DocumentStatusSkipReason.AttachedToWorkflow:
      return one ? 'is a signing file in a workflow' : 'are signing files in a workflow';
    case Apollo.DocumentStatusSkipReason.CannotSignTemplate:
      return one
        ? "is a template and can't be marked signed"
        : "are templates and can't be marked signed";
    case Apollo.DocumentStatusSkipReason.NotAuthorized:
      return one ? "is a document you can't edit" : "are documents you can't edit";
    case Apollo.DocumentStatusSkipReason.NotPdf:
      return one ? 'is not a PDF' : 'are not PDFs';
    case Apollo.DocumentStatusSkipReason.SignedOrCertificate:
      return one ? 'is signed or a certificate' : 'are signed or certificates';
    case Apollo.DocumentStatusSkipReason.SigningWithinAline:
      return one
        ? 'has its status set by an Aline workflow'
        : 'have their status set by an Aline workflow';
    case Apollo.DocumentStatusSkipReason.WorkflowSentForSignature:
      return one ? 'was already sent for signature' : 'were already sent for signature';
  }
}

function joinList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}
